(() => {
  const pageSelector = '#MainContent[data-template*="raitooe-partners"]';
  let previousFocus = null;

  const getScope = (root = document) => root.matches?.(pageSelector) ? root : root.querySelector?.(pageSelector);

  function getScrollContext() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      const overflowY = getComputedStyle(wrapper).overflowY;
      if (/(auto|scroll)/.test(overflowY) && wrapper.scrollHeight > wrapper.clientHeight + 4) {
        return {
          target: wrapper,
          top: () => wrapper.scrollTop,
          range: () => wrapper.scrollHeight - wrapper.clientHeight
        };
      }
    }
    return {
      target: window,
      top: () => window.scrollY || document.documentElement.scrollTop,
      range: () => document.documentElement.scrollHeight - window.innerHeight
    };
  }

  function initReveal(scope) {
    const items = Array.from(scope.querySelectorAll('.reveal:not([data-partners-reveal-bound])'));
    if (!items.length) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.partnersRevealBound = 'true';
        item.classList.add('is-visible');
      });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    items.forEach((item) => {
      item.dataset.partnersRevealBound = 'true';
      observer.observe(item);
    });
  }

  function initProgress(scope) {
    const bar = scope.querySelector('[data-partners-progress]');
    if (!bar || bar.dataset.partnersProgressBound === 'true') return;
    bar.dataset.partnersProgressBound = 'true';
    const scroll = getScrollContext();
    let ticking = false;
    const update = () => {
      const range = scroll.range();
      const progress = range > 0 ? Math.min(1, Math.max(0, scroll.top() / range)) : 0;
      bar.style.width = `${progress * 100}%`;
      ticking = false;
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    scroll.target.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  }

  function applyRole(panel, tab) {
    const tabs = Array.from(tab.closest('[data-role-tabs]').querySelectorAll('[data-partner-role]'));
    tabs.forEach((candidate) => {
      const active = candidate === tab;
      candidate.classList.toggle('active', active);
      candidate.setAttribute('aria-selected', String(active));
      candidate.tabIndex = active ? 0 : -1;
    });
    panel.setAttribute('aria-labelledby', tab.id);
    const fields = {
      track: '[data-role-panel-track]',
      title: '[data-role-panel-title]',
      description: '[data-role-panel-description]',
      experience: '[data-role-panel-experience]',
      proof: '[data-role-panel-proof]',
      first: '[data-role-panel-first]'
    };
    Object.entries(fields).forEach(([key, selector]) => {
      const target = panel.querySelector(selector);
      if (target) target.textContent = tab.dataset[key] || '';
    });
    const applyButton = panel.querySelector('[data-open-partner-entry]');
    if (applyButton) applyButton.dataset.roleChoice = tab.dataset.roleKey || '';
  }

  function initRoleMatchers(scope) {
    scope.querySelectorAll('[data-role-matcher]').forEach((matcher) => {
      if (matcher.dataset.partnersRoleBound === 'true') return;
      matcher.dataset.partnersRoleBound = 'true';
      const tabs = Array.from(matcher.querySelectorAll('[data-partner-role]'));
      const panel = matcher.querySelector('[data-role-panel]');
      if (!tabs.length || !panel) return;
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => applyRole(panel, tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          tabs[next].focus();
          applyRole(panel, tabs[next]);
        });
      });
      applyRole(panel, tabs.find((tab) => tab.classList.contains('active')) || tabs[0]);
    });
  }

  function initOpenings(scope) {
    scope.querySelectorAll('[data-partner-openings]').forEach((openings) => {
      if (openings.dataset.partnersOpeningsBound === 'true') return;
      openings.dataset.partnersOpeningsBound = 'true';
      const filters = Array.from(openings.querySelectorAll('[data-opening-filter]'));
      const items = Array.from(openings.querySelectorAll('[data-opening]'));
      const count = openings.querySelector('[data-opening-count]');
      filters.forEach((button) => {
        button.addEventListener('click', () => {
          let visible = 0;
          filters.forEach((candidate) => {
            const active = candidate === button;
            candidate.classList.toggle('active', active);
            candidate.setAttribute('aria-pressed', String(active));
          });
          items.forEach((item) => {
            const show = button.dataset.openingFilter === 'all' || item.dataset.category === button.dataset.openingFilter;
            item.hidden = !show;
            if (show) visible += 1;
          });
          if (count) count.textContent = String(visible);
        });
      });
      openings.querySelectorAll('[data-opening-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          const detail = document.getElementById(button.getAttribute('aria-controls'));
          if (!detail) return;
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          const label = button.querySelector('span');
          if (label) label.textContent = expanded ? button.dataset.openLabel : button.dataset.closeLabel;
          detail.hidden = expanded;
        });
      });
    });
  }

  function roleLabel(form, value) {
    return form.querySelector(`input[name="partner_role"][value="${CSS.escape(value)}"]`)?.dataset.label || value;
  }

  function setStep(dialog, step) {
    dialog.dataset.currentStep = String(step);
    dialog.querySelectorAll('[data-form-step]').forEach((panel) => {
      const active = Number(panel.dataset.formStep) === step;
      panel.hidden = !active;
      panel.classList.toggle('active', active);
    });
    dialog.querySelectorAll('[data-progress-step]').forEach((item) => {
      item.classList.toggle('active', Number(item.dataset.progressStep) <= step);
    });
    const back = dialog.querySelector('[data-partner-prev]');
    const next = dialog.querySelector('[data-partner-next]');
    const submit = dialog.querySelector('[data-partner-submit]');
    if (back) back.hidden = step === 1;
    if (next) next.hidden = step === 3;
    if (submit) submit.hidden = step !== 3;
  }

  function clearFieldError(field) {
    field.removeAttribute('aria-invalid');
    const error = field.closest('.field')?.querySelector('.field-error');
    if (error) error.textContent = '';
  }

  function fieldMessage(field) {
    if (field.validity.valueMissing) return field.dataset.requiredMessage || 'この項目を入力してください。';
    if (field.validity.typeMismatch) return field.dataset.formatMessage || '入力形式を確認してください。';
    if (field.validity.tooShort) return `${field.minLength}文字以上で入力してください。`;
    return field.checkValidity() ? '' : '入力内容を確認してください。';
  }

  function validateStepTwo(dialog) {
    const fields = Array.from(dialog.querySelectorAll('[data-form-step="2"] input, [data-form-step="2"] select, [data-form-step="2"] textarea'));
    let firstInvalid = null;
    fields.forEach((field) => {
      const message = fieldMessage(field);
      const error = field.closest('.field')?.querySelector('.field-error');
      if (message) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
      if (error) error.textContent = message;
      if (message && !firstInvalid) firstInvalid = field;
    });
    firstInvalid?.focus();
    return !firstInvalid;
  }

  function updateSummary(dialog) {
    const form = dialog.querySelector('[data-partners-entry-form]');
    const summary = dialog.querySelector('[data-partners-summary]');
    if (!form || !summary) return;
    const data = new FormData(form);
    const rows = [
      [form.dataset.roleSummary, roleLabel(form, data.get('partner_role') || '')],
      [form.dataset.entitySummary, data.get('partner_entity') || ''],
      [form.dataset.nameSummary, data.get('partner_name') || ''],
      [form.dataset.emailSummary, data.get('partner_email') || ''],
      [form.dataset.baseSummary, data.get('partner_base') || ''],
      [form.dataset.experienceSummary, data.get('partner_experience') || ''],
      [form.dataset.portfolioSummary, data.get('partner_portfolio') || ''],
      [form.dataset.messageSummary, data.get('partner_message') || '']
    ];
    const list = document.createElement('dl');
    rows.forEach(([label, value]) => {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = value;
      row.append(term, description);
      list.append(row);
    });
    summary.replaceChildren(list);
  }

  function chooseRole(dialog, role) {
    if (!role) return;
    const input = dialog.querySelector(`input[name="partner_role"][value="${CSS.escape(role)}"]`);
    if (input) input.checked = true;
  }

  function openDialog(scope, role) {
    const dialog = scope.querySelector('[data-partners-dialog]');
    if (!dialog) return;
    if (dialog.querySelector('[data-partners-entry-form]')?.getAttribute('aria-busy') === 'true') return;
    previousFocus = document.activeElement;
    const form = dialog.querySelector('[data-partners-entry-form]');
    const success = dialog.querySelector('[data-partners-success]');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
    chooseRole(dialog, role);
    setStep(dialog, 1);
    document.body.classList.add('partners-dialog-open');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    requestAnimationFrame(() => dialog.querySelector('[data-close-partner-dialog]')?.focus());
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('partners-dialog-open');
    if (previousFocus instanceof HTMLElement) previousFocus.focus();
  }

  function initForm(scope) {
    const dialog = scope.querySelector('[data-partners-dialog]');
    if (!dialog || dialog.dataset.partnersFormBound === 'true') return;
    dialog.dataset.partnersFormBound = 'true';
    const form = dialog.querySelector('[data-partners-entry-form]');
    const next = dialog.querySelector('[data-partner-next]');
    const back = dialog.querySelector('[data-partner-prev]');
    const message = dialog.querySelector('[data-partner-message]');
    const counter = dialog.querySelector('[data-partner-message-count]');

    next?.addEventListener('click', () => {
      const step = Number(dialog.dataset.currentStep || 1);
      if (step === 1) {
        const role = form.querySelector('input[name="partner_role"]:checked');
        const entity = form.querySelector('input[name="partner_entity"]:checked');
        const roleError = dialog.querySelector('[data-role-error]');
        const entityError = dialog.querySelector('[data-entity-error]');
        if (roleError) roleError.textContent = role ? '' : form.dataset.roleRequired;
        if (entityError) entityError.textContent = entity ? '' : form.dataset.entityRequired;
        if (!role || !entity) {
          if (!role) form.querySelector('input[name="partner_role"]')?.focus();
          else form.querySelector('input[name="partner_entity"]')?.focus();
          return;
        }
      }
      if (step === 2 && !validateStepTwo(dialog)) return;
      const newStep = Math.min(3, step + 1);
      if (newStep === 3) updateSummary(dialog);
      setStep(dialog, newStep);
      dialog.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });

    back?.addEventListener('click', () => {
      setStep(dialog, Math.max(1, Number(dialog.dataset.currentStep || 1) - 1));
      dialog.scrollTo({ top: 0, behavior: 'auto' });
    });

    message?.addEventListener('input', () => {
      if (counter) counter.textContent = String(message.value.length);
      clearFieldError(message);
    });

    form?.querySelectorAll('input, select, textarea').forEach((field) => {
      field.addEventListener('change', () => clearFieldError(field));
    });

    let pendingPayload = '';
    let requestId = '';
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.getAttribute('aria-busy') === 'true') return;
      if (Number(dialog.dataset.currentStep) !== 3) {
        next?.click();
        return;
      }
      if (!form.querySelector('[name="partner_role"]:checked') || !form.querySelector('[name="partner_entity"]:checked')) {
        setStep(dialog, 1);
        next?.click();
        return;
      }
      setStep(dialog, 2);
      if (!validateStepTwo(dialog)) return;
      setStep(dialog, 3);
      const consent = form.querySelector('[data-partner-consent]');
      const consentError = form.querySelector('[data-consent-error]');
      if (!consent?.checked) {
        if (consentError) consentError.textContent = form.dataset.consentRequired;
        consent?.focus();
        return;
      }
      if (consentError) consentError.textContent = '';
      const errorBox = form.querySelector('[data-partner-submit-error]');
      const submit = form.querySelector('[data-partner-submit]');
      const label = submit.textContent;
      if (errorBox) errorBox.textContent = '';
      const data = new FormData(form);
      data.set('partner_role_label', roleLabel(form, data.get('partner_role')));
      data.set('consent_text', form.querySelector('.consent span').textContent.trim());
      data.set('landing_url', location.href);
      const payload = JSON.stringify(Array.from(data.entries()));
      if (payload !== pendingPayload) {
        requestId = crypto.randomUUID();
        pendingPayload = payload;
      }
      data.set('request_id', requestId);
      data.set('token', 'c046e9b0778f1cb765942d6523dbb816');
      form.setAttribute('aria-busy', 'true');
      const controls = Array.from(form.querySelectorAll('input, select, textarea, button'));
      controls.forEach(control => { control.disabled = true; });
      submit.textContent = form.dataset.sendingLabel;
      try {
        const response = await fetch('https://tj.coopez.cn/partner/submit', {
          method: 'POST', body: data, credentials: 'omit', signal: AbortSignal.timeout(20000)
        });
        const result = await response.json();
        if (!response.ok || result.code !== 0) throw new Error(result.msg || form.dataset.submitError);
        form.hidden = true;
        const success = dialog.querySelector('[data-partners-success]');
        if (success) success.hidden = false;
        form.reset();
        pendingPayload = '';
        requestId = '';
        if (counter) counter.textContent = '0';
        dialog.scrollTo({ top: 0, behavior: 'auto' });
        success?.querySelector('button')?.focus();
      } catch (error) {
        if (errorBox) {
          errorBox.textContent = error.name === 'Error' ? error.message : form.dataset.submitError;
          errorBox.focus();
        }
      } finally {
        controls.forEach(control => { control.disabled = false; });
        submit.textContent = label;
        form.removeAttribute('aria-busy');
      }
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(dialog);
    });
  }

  function init(root = document) {
    const scope = getScope(root);
    if (!scope) return;
    document.body.classList.add('raitooe-partners-template');
    initReveal(scope);
    initProgress(scope);
    initRoleMatchers(scope);
    initOpenings(scope);
    initForm(scope);
  }

  document.addEventListener('click', (event) => {
    const scope = getScope();
    if (!scope) return;
    const opener = event.target.closest('[data-open-partner-entry]');
    if (opener && scope.contains(opener)) {
      openDialog(scope, opener.dataset.roleChoice || '');
      return;
    }
    const closer = event.target.closest('[data-close-partner-dialog]');
    if (closer) closeDialog(closer.closest('[data-partners-dialog]'));
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
